import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchEventLogs } from "redux/CreateDataset/actions";

const DEFAULT_POLLING_INTERVAL = 2000;

export function useEventLogsPolling(
  datasetId?: string,
  stopCondition?: () => boolean,
  isUsageReportEnabled?: boolean,
) {
  const dispatch = useDispatch();
  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopConditionRef = useRef(stopCondition);
  useEffect(() => {
    stopConditionRef.current = stopCondition;
  }, [stopCondition]);

  const stopEventLogsPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  useEffect(() => {
    return () => stopEventLogsPolling();
  }, [stopEventLogsPolling]);

  const fetchEventLogsOnce = useCallback(() => {
    if (!datasetId) return;
    dispatch(fetchEventLogs(datasetId, false, isUsageReportEnabled) as any);
  }, [dispatch, datasetId, isUsageReportEnabled]);

  const fetchAndCheckStop = useCallback(() => {
    if (!datasetId) {
      stopEventLogsPolling();
      return;
    }

    dispatch(fetchEventLogs(datasetId, false, isUsageReportEnabled) as any);

    if (stopConditionRef.current?.()) {
      stopEventLogsPolling();
    }
  }, [dispatch, datasetId, stopEventLogsPolling, isUsageReportEnabled]);

  const startContinuousEventLogsPolling = useCallback(() => {
    if (!datasetId || intervalRef.current) return;

    intervalRef.current = setInterval(
      fetchAndCheckStop,
      DEFAULT_POLLING_INTERVAL,
    );

    setIsPolling(true);
    fetchAndCheckStop();
  }, [datasetId, fetchAndCheckStop]);

  return {
    startContinuousEventLogsPolling,
    stopEventLogsPolling,
    fetchEventLogsOnce,
    isPolling,
  };
}
